
import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { CurrentUserStore } from '@app/user/store/current-user/current-user.store';
import { SubscriptionMode } from '@app/core/model/user/subscription-modes';
import { BaseService } from '../base.service';
import { filter } from 'rxjs/operators';
import { InstrumentPlatforms, InstrumentTypes } from '@app/core/model/runs/instrument';
import { IRun } from '@app/core/model/runs/run';
import { IQScoreStat } from '@app/core/model/runs/seq-stats';

@Injectable({
  providedIn: 'root'
})
export class CodeFeaturesService extends BaseService {
  public static readonly MICROARRAY = 'Microarray';
  public static readonly MICROARRAY_CUSTOM_CONFIG = 'MicroarrayCustomConfig';
  public static readonly MICROARRAY_IMPORT_IDATS = 'MicroarrayImportIdats';
  public static readonly MICROARRAY_DATA_MANAGEMENT = 'MicroarrayDataManagement';
  public static readonly MICROARRAY_ANALYSIS_PLANNING = 'MicroarrayAnalysisPlanning';
  public static readonly BIOSAMPLE_REGISTRY = 'BioSampleRegistry';
  public static readonly APPLICATIONS_PROMOTION = 'ApplicationsPromotion';
  public static readonly APPCATEGORY_DEFAULT_ALL_ENABLED = 'AppCategoryDefault_All';
  public static readonly COMPUTE_METERING = 'ComputeMeteringEnabled';
  public static readonly FREE_TRIAL = 'FreeTrial';
  public static readonly FREE_TRIAL_DISABLED = 'FreeTrialTemporarilyDisabled';
  public static readonly POOL_LIBRARY_PAGE_HIDE_YIELDS = 'PoolLibraryPageHideYields';
  public static readonly SUBSCRIPTIONSERVICEMODE_LEGACYZOURA = 'SubscriptionMode_LegacyZuora';
  public static readonly SUBSCRIPTIONSERVICEMODE_ZEUS = 'SubscriptionMode_Zeus';
  public static readonly SUBSCRIPTIONSERVICEMODE_PLATFORMAPI = 'SubscriptionMode_PlatformApi';
  public static readonly SUBSCRIPTIONSERVICEMODE_PLATFORMAPI_V2 = 'SubscriptionMode_PlatformV2';
  public static readonly ARCHIVING = 'Archiving';
  public static readonly RUN_ZIPPING = 'RunZipping';
  public static readonly DISPLAY_NEW_UI_FOR_APP_LAUNCH_PAGE = 'DisplayNewUIForAppLaunchPage';
  // tslint:disable-next-line: variable-name
  public static readonly REDIRECT_404s_TO_CLOUD_WEB = 'Redirect404sToCloudWeb';

  public static readonly RUN_PLANNING_SHOW_LANE_USAGE_CHECKBOX_MULTI_ANALYSIS = "RunPlanning_ShowLaneUsageCheckbox_MultiAnalysis";
  public static readonly SHOW_INSTRUMENT_TERRA = 'RunPlanning_SequencerTerra';
  public static readonly ANALYSIS_CONFIG_TEMPLATE = 'AnalysisConfigTemplate';
  public static readonly ANALYSIS_CONFIG_TEMPLATE_TOGGLE_SWITCH = 'AnalysisConfigTemplateToggleSwitch';
  public static readonly REFERENCE_FILE = 'ReferenceFile';
  public static readonly ANALYSIS_DEFINITION = 'CustomAnalysisDefinition';  
  public static readonly PREP_TAB = "PrepTab";
  public static readonly RUN_PLANNING_IMPORT_FROM_ACT = 'RunPlanning_ImportFromAct';
  public static readonly SAVE_LOCAL_PLAN_RUN = 'SaveLocalPlanRun';
  public static readonly SHOW_OVERRIDE_CYCLES_FOR_ALL = 'ShowOverrideCyclesForAllInstruments';
  public static readonly SAMPLE_PROJECT_MUTUALLY_EXCLUSIVE = 'RunPlanning_SampleProject_MutuallyExclusive';
  public static readonly RUN_PLANNING_SYSTEM_TAGS = 'RunPlanning_SystemTags';

  public static readonly CUSTOM_KIT = 'CustomKit';
  public static readonly GDS_DELETE = 'GdsDelete';
  public static readonly WEB_ROUTE_LOGGING = 'WebRouteLogging';
  public static readonly RUN_INSIGHTS_DISABLED = 'RunInsightsDisabled';
  public static readonly SHOW_REGISTER_FASTQS_TO_STRATUS_SETTING = 'ShowRegisterFastqsToStratusSetting';
  public static readonly SHOW_UPLOAD_ICA_TOGGLE = 'EnableShowUploadIcaToggle';
  public static readonly NULLIFY_BIOSAMPLE_DEFAULT_PROJECT_ENABLED= 'NullifyBioSampleDefaultProject';
  public static readonly IGNORE_INSTRUMENT_SEQUENCING_STATS = 'IgnoreInstrumentSequencingStats';
  public static readonly RESOURCES_CUSTOM_IAK_EDITOR = 'ResourcesCustomIAKEditor';
  public static readonly NO_ANALYSIS = 'RunPlanning_NoAnalysis';
  public static readonly SHOW_APP_COST_DETAILS = 'ShowAppCostDetails';
  public static readonly ICA_ANALYSIS_FETCH_COMPUTE_COST = 'IcaAnalysisFetchComputeCost';
  public static readonly ICA_ORCHESTRATED_ANALYSES_SECTION = 'IcaOrchestratedAnalysesSection';
  public static readonly PUBLIC_TO_ENTERPRISE_TRANSFER_FOR_SAME_USER_ENABLED = 'PublicToEnterpriseTransferForSameUserEnabled';
  public static readonly USE_ICA_STORAGE_CONFIGURATION = 'UseIcaStorageConfiguration';
  public static readonly LAUNCH_ICA_PIPELINE_WITH_V1_SAMPLES_DISABLED = 'LaunchIcaPipelineWithV1SamplesDisabled';

  /** we were not able to use these static values in current use store
  * because of circular dependency issue with currentUserStore
  */
  public static readonly WALK_ME_ENABLED = "WalkMeEnabled";
  public static readonly WALK_ME_TEST_MODE = "WalkMeTestMode";
  public static readonly GOOGLE_TAG_MANAGER = "GoogleTagManager";

  public static readonly USER_INTERFACE_BETA_PREVIEW_ACCESSIBLE = "UserInterfaceBetaPreviewAccessible";
  public static readonly A8_APP_LAUNCH_FORM_ENABLED = "A8AppLaunchFormEnabled";
  public static readonly A8_OAUTH_ENABLED = "A8OAuthEnabled";
  public static readonly LEGACY_NAVBAR_ENABLED = 'LegacyNavbarEnabled';
  public static readonly SUBSCRIPTION_BANNER_ENABLED = 'SubscriptionBannerEnabled';
  public static readonly FILE_CHOOSER_SORT_BY_MODIFIED_ON = 'FileChooserSortByModifiedOn';

  private codeFeatures: string[];
  /** Emits once (and on every subsequent update) when code features have been loaded from the store. */
  public readonly featuresLoaded$ = new ReplaySubject<void>();

  constructor(private currentUserStore: CurrentUserStore) {
    super();
    this.init();
  }

  private init() {
    this.codeFeatures = [];
    this.subs.sink = this.currentUserStore.stateChanged.pipe(
      filter(state => state != null && state.currentUserCodeFeatures != null)
    ).subscribe({
      next: state => {
        this.codeFeatures = state ? state.currentUserCodeFeatures : [];
        this.featuresLoaded$.next();
      }
    });
  }

  get microarrayIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.MICROARRAY);
  }

  get microarrayCustomConfigIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.MICROARRAY_CUSTOM_CONFIG);
  }

  get microarrayImportIdatsIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.MICROARRAY_IMPORT_IDATS);
  }

  get microarrayDataManagementIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.MICROARRAY_DATA_MANAGEMENT);
  }

  get microarrayAnalysisPlanningIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.MICROARRAY_ANALYSIS_PLANNING);
  }

  get applicationsPromotionIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.APPLICATIONS_PROMOTION);
  }

  get isAppCategoryDefault_AllEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.APPCATEGORY_DEFAULT_ALL_ENABLED);
  }

  get freeTrialIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.FREE_TRIAL);
  }

  get freeTrialTemporarilyDisabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.FREE_TRIAL_DISABLED);
  }

  get runInsightsDisabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.RUN_INSIGHTS_DISABLED);
  }

  get poolLibraryPageHideYieldsIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.POOL_LIBRARY_PAGE_HIDE_YIELDS);
  }

  get archivingIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.ARCHIVING);
  }

  get runZippingIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.RUN_ZIPPING);
  }

  get gdsDeleteIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.GDS_DELETE);
  }

  get displayNewUIForAppLaunchPageIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.DISPLAY_NEW_UI_FOR_APP_LAUNCH_PAGE);
  }

  get showLaneUsageCheckboxForMultiAnalysisEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.RUN_PLANNING_SHOW_LANE_USAGE_CHECKBOX_MULTI_ANALYSIS);
  }

  get showSequencerTerraInstrument(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.SHOW_INSTRUMENT_TERRA);
  }

  get analysisConfigTemplateEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.ANALYSIS_CONFIG_TEMPLATE);
  }

  get analysisConfigTemplateToggleIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.ANALYSIS_CONFIG_TEMPLATE_TOGGLE_SWITCH);
  }

  get referenceFileEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.REFERENCE_FILE);
  }
  
  get analysisDefinitionEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.ANALYSIS_DEFINITION);
  }

  get prepTabEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.PREP_TAB);
  }

  get importFromActEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.RUN_PLANNING_IMPORT_FROM_ACT);
  }

  get saveLocalPlanRunEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.SAVE_LOCAL_PLAN_RUN);
  }

  get showOverrideCyclesForAllInstruments(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.SHOW_OVERRIDE_CYCLES_FOR_ALL);
  }

  get customKitIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.CUSTOM_KIT);
  }

  get showIcaRunContextSwitchSetting(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.SHOW_UPLOAD_ICA_TOGGLE);
  }

  get sampleProjectMutuallyExclusiveEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.SAMPLE_PROJECT_MUTUALLY_EXCLUSIVE);
  }

  get runPlanningSystemTagsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.RUN_PLANNING_SYSTEM_TAGS);
  }

  /**
   * User has a wallet for storing an icredit balance if:
   * a) we're not using a Zuora alternative, i.e., everything is in Zuora, or
   * b) one of the Zeus or Platform SS code features is on AND the LegacyZuora code feature is on, or
   * c) Platform subscription service V2 is on. This keeps the Angular wallet gate in parity with
   *    backend StoreLogic.HasWallet for Platform V2 subscriptions.
   */
  get hasWallet(): boolean {
    const zeusSubscriptionsEnabled = this.codeFeatures.includes(CodeFeaturesService.SUBSCRIPTIONSERVICEMODE_ZEUS);
    const platformSubscriptionsEnabled = this.codeFeatures.includes(CodeFeaturesService.SUBSCRIPTIONSERVICEMODE_PLATFORMAPI);
    const platformSubscriptionsV2Enabled = this.codeFeatures.includes(CodeFeaturesService.SUBSCRIPTIONSERVICEMODE_PLATFORMAPI_V2);
    const isEverythingInZuora = !zeusSubscriptionsEnabled && !platformSubscriptionsEnabled;
    const isWalletOnlyInZuora = this.codeFeatures.includes(CodeFeaturesService.SUBSCRIPTIONSERVICEMODE_LEGACYZOURA);
    return isEverythingInZuora || isWalletOnlyInZuora || platformSubscriptionsV2Enabled;
  }

  get getSubscriptionMode(): SubscriptionMode {
    if (this.codeFeatures.includes(CodeFeaturesService.SUBSCRIPTIONSERVICEMODE_PLATFORMAPI)) {
      return SubscriptionMode.PLATFORM_API;
    }

    if (this.codeFeatures.includes(CodeFeaturesService.SUBSCRIPTIONSERVICEMODE_ZEUS)) {
      return SubscriptionMode.ZEUS;
    }

    // LEGACYZUORA or none
    return SubscriptionMode.LEGACY_ZUORA;
  }

  get isWalkMeEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.WALK_ME_ENABLED);
  }

  get walkMeTestMode(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.WALK_ME_TEST_MODE);
  }

  get isGoogleTagMgrEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.GOOGLE_TAG_MANAGER);
  }

  get webRouteLoggingIsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.WEB_ROUTE_LOGGING);
  }

  get bioSampleRegistryEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.BIOSAMPLE_REGISTRY);
  }

  get showRegisterFastqsToStratusSetting(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.SHOW_REGISTER_FASTQS_TO_STRATUS_SETTING);
  }

  get nullifyBioSampleDefaultProjectEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.NULLIFY_BIOSAMPLE_DEFAULT_PROJECT_ENABLED);
  }

  get isUserInterfaceBetaPreviewAccessible(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.USER_INTERFACE_BETA_PREVIEW_ACCESSIBLE);
  }

  ignoreInstrumentSequencingStatsEnabled(instrumentPlatform: string): boolean {
    let codeFeatureInstrumentName: string;
    switch (instrumentPlatform) {
      case InstrumentPlatforms.NOVASEQ_X_SERIES:
        // code feature was already in use before instrument name was finalized
        codeFeatureInstrumentName = "NovaSeqX";
        break;
      case InstrumentPlatforms.MISEQ_I100_SERIES:
      case InstrumentPlatforms.SEQUENCER_TERRA:
        codeFeatureInstrumentName = instrumentPlatform;
        break;
      default:
        return false;
    }
    return this.codeFeatures.includes(`${CodeFeaturesService.IGNORE_INSTRUMENT_SEQUENCING_STATS}.${codeFeatureInstrumentName}`);
  }

  get resourcesCustomIAKEditorEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.RESOURCES_CUSTOM_IAK_EDITOR);
  }

  get redirect404sToCloudWebEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.REDIRECT_404s_TO_CLOUD_WEB);
  }

  get noAnalysisEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.NO_ANALYSIS);
  }

  get a8AppLaunchFormEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.A8_APP_LAUNCH_FORM_ENABLED);
  }

  get showAppCostDetailsEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.SHOW_APP_COST_DETAILS);
  }

  get icaAnalysisFetchComputeCostEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.ICA_ANALYSIS_FETCH_COMPUTE_COST);
  }

  get a8OAuthEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.A8_OAUTH_ENABLED);
  }

  get icaOrchestratedAnalysesSectionEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.ICA_ORCHESTRATED_ANALYSES_SECTION);
  }

  get legacyNavbarEnabled(): boolean {
    // Radiance is the default navbar; this opt-out flag forces the legacy navbar back on.
    // Returns false before code features load (empty array), so the first paint is the
    // Radiance navbar — this is what eliminates the flash of the old navbar.
    return this.codeFeatures.includes(CodeFeaturesService.LEGACY_NAVBAR_ENABLED);
  }

  get subscriptionBannerEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.SUBSCRIPTION_BANNER_ENABLED);
  }

  get publicToEnterpriseTransferForSameUserEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.PUBLIC_TO_ENTERPRISE_TRANSFER_FOR_SAME_USER_ENABLED);
  }

  get useIcaStorageConfigurationEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.USE_ICA_STORAGE_CONFIGURATION);
  }

  /**
   * When enabled, ICA Pipeline (ICA2) apps restrict input to biosamples only — the v1 sample
   * input option is disabled. When off, users may launch ICA Pipeline apps with v1 sample input.
   */
  get launchIcaPipelineWithV1SamplesDisabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.LAUNCH_ICA_PIPELINE_WITH_V1_SAMPLES_DISABLED);
  }

  /** Fallback: restore legacy ModifiedOn default sort on file choosers. */
  get fileChooserSortByModifiedOnEnabled(): boolean {
    return this.codeFeatures.includes(CodeFeaturesService.FILE_CHOOSER_SORT_BY_MODIFIED_ON);
  }
  
  /**
   * Returns true if the given Q-score has an enabled code feature.
   * - Exact threshold scores (Q40, Q50…): checks ShowQ{N}Stats  (e.g. ShowQ40Stats)
   * - Q-score-X values (Q41–Q49…):        checks ShowQ{N}XStats (e.g. ShowQ4XStats)
   */
  isHigherQScoreStatsEnabled(qScore: number): boolean {
    if (qScore % 10 === 0) {
      return this.codeFeatures.includes(`ShowQ${qScore}Stats`);
    }
    const leading = Math.floor(qScore / 10);
    return this.codeFeatures.includes(`ShowQ${leading}XStats`);
  }

  /**
   * Returns the list of Q-score thresholds above Q30 that have a CF enabled,
   * in descending order (highest first). Iterates Q40–Q90 in steps of 10.
   */
  getEnabledHigherQScores(min = 40, max = 90, step = 10): number[] {
    const result: number[] = [];
    for (let q = max; q >= min; q -= step) {
      if (this.isHigherQScoreStatsEnabled(q)) {
        result.push(q);
      }
    }
    return result;
  }

  /**
   * Picks the highest CF-enabled Q-score that has data in the run's QScoreStats.
   * Falls back to Q30 when no higher Q-score qualifies.
   */
  resolveActiveQScore(qScoreStats: IQScoreStat[]): number {
    const enabledQScores = this.getEnabledHigherQScores();
    const presentQScores = new Set(qScoreStats.map(s => s.QScore));
    const match = enabledQScores.find(q => presentQScores.has(q));
    return match !== undefined ? match : 30;
  }

  /**
   * Returns true if SequencerTerraShowQ30AsQ4X is enabled.
   * When true for a SequencerTerra run, Q30 is displayed as Q40 — takes precedence
   * over isHigherQScoreStatsEnabled. The run stays on Q30 data; only the label changes.
   */
  isSequencerTerraShowQ30AsQ4X(): boolean {
    return this.codeFeatures.includes('SequencerTerraShowQ30AsQ4X');
  }

  /**
   * Returns the display label suffix for a given Q-score and run.
   *
   * Priority:
   * 1. SequencerTerra + SequencerTerraShowQ30AsQ4X CF + qScore === 30 → 'Q40'
   *    (takes precedence over all other CF logic)
   * 2. QX values (Q41, Q42… where qScore % 10 !== 0) → '=Q41', '=Q42', etc.
   * 3. Exact threshold Q-scores (Q30, Q40…) → '≥Q30', '≥Q40'
   */
  getQScoreDisplayLabel(qScore: number, run: IRun): string {
    const instrumentType = run && ((run.Instrument && run.Instrument.Type) || run.InstrumentType);
    const isSequencerTerra = instrumentType === InstrumentTypes.SEQUENCER_TERRA;
    // SequencerTerra alpha: Q30 temporarily displayed as Q40 — takes precedence
    if (isSequencerTerra && this.isSequencerTerraShowQ30AsQ4X() && qScore === 30) {
      return 'Q40';
    }
    // In-between Q-scores: Q41 → '=Q41', Q42 → '=Q42', etc.
    if (qScore % 10 !== 0) {
      return `=Q${qScore}`;
    }
    // Exact threshold Q-scores: Q30 → '≥Q30', Q40 → '≥Q40', etc.
    return `\u2265Q${qScore}`;
  }

}
